import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import TargetWidget from '../components/TargetWidget';
import { fmtDate } from '../utils/dates';

const KERALA_DISTRICTS = [
  'Thiruvananthapuram','Kollam','Pathanamthitta','Alappuzha','Kottayam',
  'Idukki','Ernakulam','Thrissur','Palakkad','Malappuram',
  'Kozhikode','Wayanad','Kannur','Kasaragod',
];

const COMMISSION_PER_LEAD = 5;

const getToken = () => localStorage.getItem('token');

const inputCls =
  'w-full rounded-2xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm font-medium text-gray-800 shadow-sm outline-none transition focus:border-yellow-400 focus:ring-2 focus:ring-yellow-100 placeholder:text-gray-400';

const Lbl = ({ children, opt }) => (
  <p className="mb-1.5 text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">
    {children}
    {opt && <span className="ml-1 normal-case font-semibold text-gray-300">(optional)</span>}
  </p>
);

// ─── KPI Card ──────────────────────────────────────────────────────────────────
const KpiCard = ({ label, value, sub, accent, icon }) => (
  <div className={`rounded-2xl border bg-white p-4 flex items-center gap-4 shadow-sm ${accent ? 'border-black' : 'border-gray-100'}`}>
    <div className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl text-xl ${accent ? 'bg-black text-yellow-400' : 'bg-gray-100 text-gray-600'}`}>
      {icon}
    </div>
    <div className="min-w-0">
      <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">{label}</p>
      <p className={`text-2xl font-black leading-none mt-0.5 ${accent ? 'text-black' : 'text-gray-900'}`}>{value}</p>
      {sub && <p className="text-[11px] text-gray-400 mt-0.5">{sub}</p>}
    </div>
  </div>
);

// ─── Photo Preview Strip ───────────────────────────────────────────────────────
const PhotoStrip = ({ urls, onRemove }) => {
  if (!urls || urls.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {urls.map((url, i) => (
        <div key={i} className="relative group">
          <img src={url} alt="" className="h-16 w-16 rounded-xl object-cover border border-gray-200" />
          {onRemove && (
            <button
              type="button"
              onClick={() => onRemove(i)}
              className="absolute -top-1.5 -right-1.5 hidden group-hover:flex h-5 w-5 items-center justify-center rounded-full bg-black text-yellow-400 text-xs font-black"
            >✕</button>
          )}
        </div>
      ))}
    </div>
  );
};

// ─── Status Badge ──────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    Pending:  'bg-gray-100 text-gray-500 border-gray-200',
    Accepted: 'bg-black text-yellow-400 border-black',
    Rejected: 'bg-red-50 text-red-600 border-red-200',
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] font-extrabold tracking-wide ${map[status] || map.Pending}`}>
      {status === 'Accepted' && '✓ '}{status === 'Rejected' && '✕ '}{status || 'Pending'}
    </span>
  );
};

// ─── Lead Detail Modal ─────────────────────────────────────────────────────────
const LeadDetailModal = ({ lead, API_URL, onClose }) => {
  if (!lead) return null;
  const outside = JSON.parse(lead.outside_board_photos || '[]');
  const office  = JSON.parse(lead.office_photos || '[]');
  const toUrl   = (p) => p ? `${API_URL}/${p.replace(/\\/g, '/')}` : '';
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4 pt-10">
      <div className="w-full max-w-lg rounded-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between rounded-t-3xl bg-black px-6 py-5">
          <div>
            <p className="text-[10px] font-extrabold uppercase tracking-widest text-yellow-400">Lead Detail</p>
            <h3 className="text-lg font-black text-white">{lead.company_name || 'Unnamed Company'}</h3>
          </div>
          <button type="button" onClick={onClose} className="rounded-xl bg-white/20 px-3 py-1.5 text-sm font-bold text-white hover:bg-white/30">✕</button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-2">
            {[
              ['Status', <StatusBadge key="s" status={lead.status} />],
              ['Contact', lead.contact_number || '—'],
              ['Place', lead.place || '—'],
              ['District', lead.district || '—'],
              ['Submitted', fmtDate(lead.created_at)],
            ].map(([l, v]) => (
              <div key={l} className="rounded-xl border border-gray-100 bg-gray-50 px-3 py-2">
                <p className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400">{l}</p>
                <div className="mt-0.5 text-sm font-semibold text-gray-800">{v}</div>
              </div>
            ))}
            {lead.current_location && (
              <div className="col-span-2 rounded-xl border border-gray-100 bg-gray-50 px-3 py-2">
                <p className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400">Location</p>
                <a href={lead.current_location} target="_blank" rel="noreferrer" className="mt-0.5 text-sm font-semibold text-yellow-600 underline hover:text-black">📍 View on Google Maps</a>
              </div>
            )}
          </div>
          {lead.notes && (
            <div className="rounded-xl border border-gray-100 bg-gray-50 px-3 py-2">
              <p className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 mb-1">Notes</p>
              <p className="text-sm text-gray-700">{lead.notes}</p>
            </div>
          )}
          {outside.length > 0 && (
            <div>
              <p className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 mb-2">Outside Board Photos</p>
              <div className="flex flex-wrap gap-2">
                {outside.map((p, i) => <a key={i} href={toUrl(p)} target="_blank" rel="noreferrer"><img src={toUrl(p)} alt="" className="h-20 w-20 rounded-xl object-cover border border-gray-200 hover:opacity-80 transition-opacity" /></a>)}
              </div>
            </div>
          )}
          {office.length > 0 && (
            <div>
              <p className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 mb-2">Office Photos</p>
              <div className="flex flex-wrap gap-2">
                {office.map((p, i) => <a key={i} href={toUrl(p)} target="_blank" rel="noreferrer"><img src={toUrl(p)} alt="" className="h-20 w-20 rounded-xl object-cover border border-gray-200 hover:opacity-80 transition-opacity" /></a>)}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Add Lead Form (Slide-out panel) ──────────────────────────────────────────
const AddLeadPanel = ({ API_URL, onDone, onClose }) => {
  const [form, setForm] = useState({
    company_name: '', contact_number: '', current_location: '', place: '', district: '', notes: '',
  });
  const [outsideFiles, setOutsideFiles] = useState([]);
  const [officeFiles, setOfficeFiles]   = useState([]);
  const [outsidePreviews, setOutsidePreviews] = useState([]);
  const [officePreviews, setOfficePreviews]   = useState([]);
  const [locating, setLocating] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const outsideRef = useRef(null);
  const officeRef  = useRef(null);

  const sf = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const addFiles = (existing, previews, newFiles, setFiles, setPreviews) => {
    const combined = [...existing, ...Array.from(newFiles)];
    setFiles(combined);
    const urls = combined.map((f) => (f instanceof File ? URL.createObjectURL(f) : f));
    setPreviews(urls);
  };

  const removeFile = (idx, files, previews, setFiles, setPreviews) => {
    const nf = files.filter((_, i) => i !== idx);
    const np = previews.filter((_, i) => i !== idx);
    setFiles(nf);
    setPreviews(np);
  };

  const getLocation = () => {
    if (!navigator.geolocation) { setError('Geolocation not supported by this browser'); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        const url = `https://maps.google.com/?q=${latitude},${longitude}`;
        setForm((p) => ({ ...p, current_location: url }));
        setLocating(false);
      },
      (err) => {
        setError('Could not get location: ' + err.message);
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
      outsideFiles.forEach((f) => fd.append('outside_board_photos', f));
      officeFiles.forEach((f) => fd.append('office_photos', f));
      await axios.post(`${API_URL}/api/field-agent/leads`, fd, {
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'multipart/form-data' },
      });
      onDone();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to submit lead');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end bg-black/50" onClick={onClose}>
      <div
        className="relative h-full w-full max-w-md overflow-y-auto bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between bg-black px-6 py-5">
          <div>
            <p className="text-[10px] font-extrabold uppercase tracking-widest text-yellow-400">Field Agent</p>
            <h2 className="text-lg font-black text-white">Add New Lead</h2>
          </div>
          <button type="button" onClick={onClose} className="rounded-xl bg-white/20 px-3 py-1.5 text-sm font-bold text-white hover:bg-white/30">✕</button>
        </div>

        <form onSubmit={onSubmit} className="p-5 space-y-5 pb-10">
          {error && <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 font-semibold">{error}</div>}

          {/* Company Name */}
          <div>
            <Lbl opt>Company Name</Lbl>
            <input value={form.company_name} onChange={sf('company_name')} placeholder="e.g. ABC Enterprises" className={inputCls} />
          </div>

          {/* Contact Number */}
          <div>
            <Lbl opt>Contact Number</Lbl>
            <input value={form.contact_number} onChange={sf('contact_number')} placeholder="+91 98765 43210" className={inputCls} />
          </div>

          {/* Place + District */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Lbl opt>Place</Lbl>
              <input value={form.place} onChange={sf('place')} placeholder="City / Town" className={inputCls} />
            </div>
            <div>
              <Lbl opt>District</Lbl>
              <select value={form.district} onChange={sf('district')} className={inputCls}>
                <option value="">Select…</option>
                {KERALA_DISTRICTS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          </div>

          {/* Current Location */}
          <div>
            <Lbl opt>Current Location (Google Maps)</Lbl>
            <div className="flex gap-2">
              <input
                value={form.current_location}
                onChange={sf('current_location')}
                placeholder="Tap button to capture location"
                className={`${inputCls} flex-1`}
                readOnly
              />
              <button
                type="button"
                onClick={getLocation}
                disabled={locating}
                className="flex-shrink-0 rounded-2xl bg-black px-4 py-2.5 text-sm font-bold text-yellow-400 hover:bg-gray-900 disabled:opacity-60 transition"
              >
                {locating ? '…' : '📍'}
              </button>
            </div>
            {form.current_location && (
              <a href={form.current_location} target="_blank" rel="noreferrer" className="mt-1 inline-block text-xs text-yellow-600 underline hover:text-black">
                ↗ Preview on Google Maps
              </a>
            )}
          </div>

          {/* Outside Board Photos */}
          <div>
            <Lbl opt>Company Outside Board Photos</Lbl>
            <button
              type="button"
              onClick={() => outsideRef.current?.click()}
              className="w-full rounded-2xl border-2 border-dashed border-gray-200 py-4 text-center text-sm text-gray-400 hover:border-yellow-400 hover:text-yellow-500 transition"
            >
              📷 Tap to add outside board photos
            </button>
            <input
              ref={outsideRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => addFiles(outsideFiles, outsidePreviews, e.target.files, setOutsideFiles, setOutsidePreviews)}
            />
            <PhotoStrip
              urls={outsidePreviews}
              onRemove={(i) => removeFile(i, outsideFiles, outsidePreviews, setOutsideFiles, setOutsidePreviews)}
            />
          </div>

          {/* Office Photos */}
          <div>
            <Lbl opt>Company Office Photos</Lbl>
            <button
              type="button"
              onClick={() => officeRef.current?.click()}
              className="w-full rounded-2xl border-2 border-dashed border-gray-200 py-4 text-center text-sm text-gray-400 hover:border-yellow-400 hover:text-yellow-500 transition"
            >
              🏢 Tap to add office photos
            </button>
            <input
              ref={officeRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => addFiles(officeFiles, officePreviews, e.target.files, setOfficeFiles, setOfficePreviews)}
            />
            <PhotoStrip
              urls={officePreviews}
              onRemove={(i) => removeFile(i, officeFiles, officePreviews, setOfficeFiles, setOfficePreviews)}
            />
          </div>

          {/* Notes */}
          <div>
            <Lbl opt>Notes</Lbl>
            <textarea value={form.notes} onChange={sf('notes')} rows={3} placeholder="Any additional information…" className={inputCls} />
          </div>

          {/* Submit */}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 rounded-2xl border border-gray-200 py-3 text-sm font-bold text-gray-600 hover:bg-gray-50 transition">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 rounded-2xl bg-black py-3 text-sm font-extrabold text-yellow-400 hover:bg-gray-900 disabled:opacity-60 transition">
              {saving ? 'Submitting…' : '+ Submit Lead'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Commission Payments Modal ─────────────────────────────────────────────────
const CommissionPaymentsModal = ({ payments, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
    <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl">
      <div className="flex items-center justify-between rounded-t-3xl bg-black px-6 py-4">
        <div>
          <p className="text-[10px] font-extrabold uppercase tracking-widest text-yellow-400">History</p>
          <h3 className="font-black text-white">Commission Payments</h3>
        </div>
        <button type="button" onClick={onClose} className="rounded-xl bg-white/20 px-3 py-1.5 text-sm font-bold text-white hover:bg-white/30">✕</button>
      </div>
      <div className="p-4 max-h-96 overflow-y-auto space-y-2">
        {payments.length === 0 ? (
          <p className="text-center text-gray-400 py-8 text-sm font-bold">No payments received yet</p>
        ) : payments.map((p) => (
          <div key={p.id} className="flex items-center justify-between rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
            <div>
              <p className="text-sm font-black text-gray-900">₹{Number(p.amount).toLocaleString('en-IN')}</p>
              {p.note && <p className="text-xs text-gray-500 mt-0.5">{p.note}</p>}
            </div>
            <p className="text-xs text-gray-400">{fmtDate(p.created_at)}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ─── Main FieldAgentDashboard ──────────────────────────────────────────────────
const FieldAgentDashboard = ({ name, API_URL }) => {
  const [leads, setLeads]                   = useState([]);
  const [summary, setSummary]               = useState(null);
  const [payments, setPayments]             = useState([]);
  const [loading, setLoading]               = useState(true);
  const [showAddPanel, setShowAddPanel]     = useState(false);
  const [detailLead, setDetailLead]         = useState(null);
  const [showPayments, setShowPayments]     = useState(false);
  const [search, setSearch]                 = useState('');
  const [statusFilter, setStatusFilter]     = useState('All');
  const [page, setPage]                     = useState(1);
  const PAGE_SIZE = 15;

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [leadsRes, summaryRes, paymentsRes] = await Promise.all([
        axios.get(`${API_URL}/api/field-agent/leads`,               { headers: { Authorization: `Bearer ${getToken()}` } }),
        axios.get(`${API_URL}/api/field-agent/summary`,             { headers: { Authorization: `Bearer ${getToken()}` } }),
        axios.get(`${API_URL}/api/field-agent/commission-payments`, { headers: { Authorization: `Bearer ${getToken()}` } }),
      ]);
      setLeads(leadsRes.data || []);
      setSummary(summaryRes.data);
      setPayments(paymentsRes.data || []);
    } catch (err) {
      console.error('FieldAgent fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = leads.filter((l) => {
    if (statusFilter !== 'All' && l.status !== statusFilter) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      return `${l.company_name} ${l.contact_number} ${l.place} ${l.district} ${l.notes}`.toLowerCase().includes(q);
    }
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage   = Math.min(Math.max(page, 1), totalPages);
  const paged      = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this lead?')) return;
    try {
      await axios.delete(`${API_URL}/api/field-agent/leads/${id}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      fetchAll();
    } catch { alert('Failed to delete'); }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f8f8f8]">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-black" />
      </div>
    );
  }

  const s = summary || { totalLeads: 0, acceptedLeads: 0, rejectedLeads: 0, totalCommission: 0, receivedCommission: 0, balanceCommission: 0 };

  return (
    <div className="min-h-screen bg-[#f8f8f8]">
      {/* Header */}
      <div className="bg-black px-4 py-6">
        <div className="mx-auto max-w-4xl">
          <p className="text-[10px] font-extrabold uppercase tracking-[0.3em] text-yellow-400">Field Agent</p>
          <h1 className="text-2xl font-black text-white">{name}</h1>
          <p className="mt-0.5 text-sm text-gray-400">Capture company leads in the field</p>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-4 py-6 space-y-6">
        <TargetWidget API_URL={API_URL} />

        {/* KPI Summary */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <KpiCard label="Total Leads"    icon="📋" value={s.totalLeads}    sub="All submitted" />
          <KpiCard label="Accepted"       icon="✓"  value={s.acceptedLeads} sub="Approved leads" accent />
          <KpiCard label="Rejected"       icon="✕"  value={s.rejectedLeads} sub="Not approved" />
          <KpiCard label="Total Commission" icon="💰" value={`₹${(s.totalCommission).toLocaleString('en-IN')}`} sub={`${s.acceptedLeads} × ₹${COMMISSION_PER_LEAD}`} />
          <div
            className="rounded-2xl border bg-white p-4 flex items-center gap-4 shadow-sm border-gray-100 cursor-pointer hover:border-yellow-400 transition-colors"
            onClick={() => setShowPayments(true)}
          >
            <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-gray-100 text-xl text-gray-600">📥</div>
            <div className="min-w-0">
              <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Received</p>
              <p className="text-2xl font-black leading-none mt-0.5 text-gray-900">₹{(s.receivedCommission).toLocaleString('en-IN')}</p>
              <p className="text-[11px] text-yellow-600 mt-0.5 font-semibold">Tap to view history</p>
            </div>
          </div>
          <KpiCard
            label="Balance"
            icon={s.balanceCommission > 0 ? '⏳' : '✓'}
            value={`₹${s.balanceCommission.toLocaleString('en-IN')}`}
            sub={s.balanceCommission > 0 ? 'Pending payout' : 'Fully paid'}
            accent={s.balanceCommission > 0}
          />
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap gap-3 items-center">
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search by company, place, district…"
            className="flex-1 min-w-[220px] rounded-2xl border border-gray-200 bg-white px-4 py-2.5 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
          />
          <div className="flex gap-2">
            {['All', 'Pending', 'Accepted', 'Rejected'].map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => { setStatusFilter(f); setPage(1); }}
                className={`rounded-xl px-3 py-1.5 text-xs font-bold transition ${statusFilter === f ? 'bg-black text-yellow-400' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}
              >
                {f}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setShowAddPanel(true)}
            className="rounded-2xl bg-black px-5 py-2.5 text-sm font-extrabold text-yellow-400 hover:bg-gray-900 transition shadow-sm"
          >
            + Add Lead
          </button>
        </div>

        {/* Leads Table */}
        {paged.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-200 py-16 text-center text-gray-400">
            <p className="text-4xl mb-3">📋</p>
            <p className="font-bold text-gray-500">No leads found</p>
            <p className="text-sm mt-1">Tap "+ Add Lead" to submit your first lead</p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
            {/* Header row */}
            <div className="grid grid-cols-[1.6fr_1.2fr_1.2fr_90px_40px] bg-black px-4 py-2.5 text-[10px] font-extrabold uppercase tracking-widest text-yellow-400">
              <span>Company</span>
              <span>Location</span>
              <span>Contact</span>
              <span className="text-center">Status</span>
              <span></span>
            </div>
            {paged.map((lead, idx) => {
              const outside = JSON.parse(lead.outside_board_photos || '[]');
              const office  = JSON.parse(lead.office_photos || '[]');
              const photoCount = outside.length + office.length;
              return (
                <div
                  key={lead.id}
                  className={`grid grid-cols-[1.6fr_1.2fr_1.2fr_90px_40px] items-center gap-x-2 px-4 py-3 border-t border-gray-100 cursor-pointer transition-colors
                    ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}
                    hover:bg-yellow-50/30`}
                  onClick={() => setDetailLead(lead)}
                >
                  <div className="min-w-0">
                    <p className="font-black text-gray-900 truncate">{lead.company_name || <span className="text-gray-400 font-normal italic">No name</span>}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {photoCount > 0 && <span className="text-[10px] text-gray-400">📷 {photoCount}</span>}
                      {lead.current_location && <span className="text-[10px] text-yellow-600">📍</span>}
                      <p className="text-[10px] text-gray-400">{fmtDate(lead.created_at)}</p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-600 truncate">
                    {[lead.place, lead.district].filter(Boolean).join(', ') || <span className="text-gray-300">—</span>}
                  </p>
                  <p className="text-xs text-gray-600 truncate">{lead.contact_number || <span className="text-gray-300">—</span>}</p>
                  <div className="flex justify-center" onClick={(e) => e.stopPropagation()}>
                    <StatusBadge status={lead.status} />
                  </div>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleDelete(lead.id); }}
                    className="text-xs text-red-300 hover:text-red-600 font-black transition-colors"
                    title="Delete"
                  >✕</button>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <button type="button" onClick={() => setPage((p) => Math.max(p - 1, 1))} disabled={safePage === 1} className="rounded-xl border px-3 py-1.5 text-sm font-bold disabled:opacity-40 hover:bg-gray-50">← Prev</button>
            <span className="text-sm text-gray-500">Page {safePage} of {totalPages}</span>
            <button type="button" onClick={() => setPage((p) => Math.min(p + 1, totalPages))} disabled={safePage === totalPages} className="rounded-xl border px-3 py-1.5 text-sm font-bold disabled:opacity-40 hover:bg-gray-50">Next →</button>
          </div>
        )}
      </div>

      {/* Modals */}
      {showAddPanel && (
        <AddLeadPanel
          API_URL={API_URL}
          onDone={() => { setShowAddPanel(false); fetchAll(); }}
          onClose={() => setShowAddPanel(false)}
        />
      )}
      {detailLead && <LeadDetailModal lead={detailLead} API_URL={API_URL} onClose={() => setDetailLead(null)} />}
      {showPayments && <CommissionPaymentsModal payments={payments} onClose={() => setShowPayments(false)} />}
    </div>
  );
};

export default FieldAgentDashboard;
